/**
 * Aba "Aguardando aprovação" de Suprimentos > Movimentos (Bloco 4, 12/09/2026).
 * Era a tela /suprimentos/ajustes-pendentes: o razão e a fila dele agora
 * moram no mesmo lugar. O movimento só existe depois da aprovação; recusar
 * não mexe em nada.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { FiAlertCircle } from "react-icons/fi";

import AsyncButton from "../../components/AsyncButton";
import ConfirmDialog from "../../components/ConfirmDialog";
import KpiStrip from "../../components/KpiStrip";
import { useAuth } from "../../context/AuthContext";
import { notificarAjustesMudou } from "../../hooks/useAjustesAguardando";
import {
  aprovarAjuste, linhasDe, listarAjustesPendentes, recusarAjuste,
} from "../../services/suprimentos";
import { fmtData, fmtMoeda, fmtQtd } from "./helpers";
import { ErrorMsg, Pill, Select, Table, TableWrapper, Textarea, TruncCell } from "./styles";
import { Carregando, Vazio } from "./_shared/Estados";

const ESTADOS = [
  { value: "AGUARDANDO", label: "Aguardando aprovação", tone: "warning" },
  { value: "APROVADO", label: "Aprovado", tone: "success" },
  { value: "RECUSADO", label: "Recusado", tone: "danger" },
];
const TONE = Object.fromEntries(ESTADOS.map((e) => [e.value, e.tone]));
const LABEL = Object.fromEntries(ESTADOS.map((e) => [e.value, e.label]));

export default function AjustesAprovacaoTab({ onMudou }) {
  const { request, isMaster, hasAllPermissions } = useAuth();
  const podeAprovar = isMaster || hasAllPermissions(["estoque:aprovar"]);
  const [linhas, setLinhas] = useState([]);
  const [estado, setEstado] = useState("AGUARDANDO");
  const [erro, setErro] = useState(null);
  const [loading, setLoading] = useState(true);
  const [recusando, setRecusando] = useState(null);
  const [motivo, setMotivo] = useState("");

  const carregar = useCallback(async () => {
    setLoading(true);
    try {
      setLinhas(linhasDe(await listarAjustesPendentes(request, { estado })));
      setErro(null);
    } catch (err) {
      setErro(err?.payload?.detalhe || err?.message || "Erro ao carregar.");
    } finally {
      setLoading(false);
    }
  }, [request, estado]);

  useEffect(() => { carregar(); }, [carregar]);

  const aposDecidir = async () => {
    notificarAjustesMudou();
    await carregar();
    await onMudou?.();
  };

  const kpis = useMemo(() => {
    const pend = linhas.filter((l) => l.estado === "AGUARDANDO");
    const valor = pend.reduce((a, l) => a + Number(l.valor_estimado || 0), 0);
    return [
      { label: "Aguardando", value: pend.length, tone: pend.length ? "warning" : "neutral" },
      { label: "Valor em espera", value: fmtMoeda(valor) },
    ];
  }, [linhas]);

  return (
    <>
      <KpiStrip items={kpis} />
      <div style={{ marginBottom: 10 }}>
        <Select value={estado} onChange={(e) => setEstado(e.target.value)}>
          <option value="">Todos</option>
          {ESTADOS.map((e) => <option key={e.value} value={e.value}>{e.label}</option>)}
        </Select>
      </div>
      {erro && <ErrorMsg><FiAlertCircle size={14} /> {erro}</ErrorMsg>}
      {loading ? <Carregando /> : linhas.length === 0 ? (
        <Vazio titulo="Nenhum ajuste neste filtro" descricao="Ajuste manual acima do limite da unidade só mexe no saldo depois que alguém aprova aqui." />
      ) : (
        <TableWrapper>
          <Table>
            <colgroup>
              <col style={{ width: "10%" }} /><col style={{ width: "26%" }} /><col style={{ width: "10%" }} />
              <col style={{ width: "14%" }} /><col style={{ width: "10%" }} /><col style={{ width: "12%" }} /><col style={{ width: "18%" }} />
            </colgroup>
            <thead><tr><th>Data</th><th>Produto</th><th>Qtd</th><th>Motivo</th><th>Valor</th><th>Situação</th><th>Ação</th></tr></thead>
            <tbody>
              {linhas.map((l) => (
                <tr key={l.id}>
                  <td>{fmtData(l.created_at)}</td>
                  <td title={`${l.produto_codigo} — ${l.produto_nome}`}>
                    <TruncCell>{l.produto_codigo} — {l.produto_nome}</TruncCell>
                    {l.observacao ? <div style={{ fontSize: 11.5, color: "var(--ink-3)" }}><TruncCell>{l.observacao}</TruncCell></div> : null}
                  </td>
                  <td>{Number(l.quantidade) > 0 ? "+" : ""}{fmtQtd(l.quantidade)}</td>
                  <td><TruncCell>{l.motivo_nome}</TruncCell></td>
                  <td>{fmtMoeda(l.valor_estimado)}</td>
                  <td>
                    <Pill $tone={TONE[l.estado]}>{LABEL[l.estado] || l.estado_display}</Pill>
                    {l.decidido_por_nome ? <div style={{ fontSize: 11.5, color: "var(--ink-3)" }}>{l.decidido_por_nome}</div> : null}
                  </td>
                  <td>
                    {podeAprovar && l.estado === "AGUARDANDO" && (
                      <div style={{ display: "flex", gap: 6 }}>
                        <AsyncButton successMessage="Ajuste aprovado." onClick={async () => { await aprovarAjuste(request, l.id); await aposDecidir(); }}>Aprovar</AsyncButton>
                        <AsyncButton variant="danger" silentSuccess onClick={async () => { setRecusando(l); setMotivo(""); }}>Recusar</AsyncButton>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </TableWrapper>
      )}

      <ConfirmDialog
        open={Boolean(recusando)}
        titulo={`Recusar o ajuste de ${recusando ? fmtQtd(recusando.quantidade) : ""} em ${recusando?.produto_codigo || ""}?`}
        descricao={(
          <>
            <span style={{ display: "block" }}>Recusar não mexe no saldo. O motivo fica registrado para quem pediu.</span>
            <Textarea rows={2} maxLength={300} placeholder="Motivo da recusa (obrigatório)" value={motivo} onChange={(e) => setMotivo(e.target.value)} style={{ marginTop: 8, width: "100%" }} />
          </>
        )}
        confirmarLabel="Sim, recusar"
        cancelarLabel="Não"
        onConfirmar={async () => {
          if (!motivo.trim()) return;
          try {
            await recusarAjuste(request, recusando.id, motivo.trim());
            setRecusando(null);
            await aposDecidir();
          } catch (err) {
            setErro(err?.payload?.detalhe || err?.message || "Erro ao recusar.");
            setRecusando(null);
          }
        }}
        onCancelar={() => setRecusando(null)}
      />
    </>
  );
}
